import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LS } from '../utils/storage';
import { apiFetch } from '../utils/api';
import { ensureFileToken, clearFileToken } from '../utils/secureFiles';
import { setAdminId } from '../utils/admin';

// 채팅·다운로드·관리자 페이지가 공유하는 로그인 상태
// status: 'checking' → 'authed' | 'anon'
const AuthContext = createContext(null);

const SESSION_KEYS = ['bcToken', 'breakChatLoginAt', 'breakChatUsername', 'breakChatUserId', 'breakChatAdminId', 'bcLoginId', 'bcPrivateChId', 'bcSessionOnly'];

// "로그인 상태 유지"를 끄고 로그인하면 브라우저 세션 쿠키(만료일 없음)를 표시로 남긴다.
// 브라우저를 닫으면 쿠키가 사라지므로, 다음에 열었을 때 쿠키가 없으면 로그아웃한다 (학교·PC방 공용 PC).
const ALIVE_COOKIE = 'bcAlive';
const setAliveCookie = () => {
    document.cookie = `${ALIVE_COOKIE}=1; path=/; SameSite=Strict${window.location.protocol === 'https:' ? '; Secure' : ''}`;
};
const hasAliveCookie = () => document.cookie.split('; ').some(c => c.startsWith(`${ALIVE_COOKIE}=`));
const clearAliveCookie = () => { document.cookie = `${ALIVE_COOKIE}=; path=/; max-age=0`; };

const toUser = (data) => ({
    userId: data.userId,
    username: data.username,
    loginId: data.loginId ?? null,
    isAdmin: !!data.isAdmin,
    privateChannelId: data.privateChannelId ?? null,
});

export function AuthProvider({ children }) {
    const [status, setStatus] = useState('checking');
    const [user, setUser] = useState(null);

    const clearSession = useCallback(() => {
        SESSION_KEYS.forEach(key => LS.remove(key));
        clearAliveCookie();
        clearFileToken();
        setUser(null);
        setStatus('anon');
    }, []);

    // 로그인 성공 시: 토큰 저장 → 이미지용 파일 토큰 발급 → 사용자 상태 반영
    const signIn = useCallback(async ({ token, remember = true, ...data }) => {
        LS.set('bcToken', token);
        LS.set('breakChatLoginAt', Date.now());
        if (remember) { LS.remove('bcSessionOnly'); clearAliveCookie(); }
        else { LS.set('bcSessionOnly', '1'); setAliveCookie(); }
        if (data.isAdmin) setAdminId(data.loginId);
        await ensureFileToken();
        setUser(toUser(data));
        setStatus('authed');
    }, []);

    // 서버가 새 토큰을 재발급한 경우 (게스트 닉네임 변경, 관리자 전체 세션 초기화 등)
    const replaceToken = useCallback((token) => {
        LS.set('bcToken', token);
        LS.set('breakChatLoginAt', Date.now());
    }, []);

    // 로그아웃: 서버의 이 세션도 끊는다 (응답을 기다리지 않아도 되도록 keepalive)
    const signOut = useCallback(() => {
        apiFetch('/api/auth/logout', { method: 'POST', keepalive: true }).catch(() => {});
        clearSession();
    }, [clearSession]);

    const updateUser = useCallback((patch) => setUser(prev => (prev ? { ...prev, ...patch } : prev)), []);

    useEffect(() => {
        (async () => {
            if (!LS.get('bcToken')) return clearSession();
            // 로그인 유지를 끈 로그인인데 브라우저를 닫았다 다시 열었으면 서버 세션까지 끊고 로그아웃
            if (LS.get('bcSessionOnly') === '1' && !hasAliveCookie()) {
                apiFetch('/api/auth/logout', { method: 'POST', keepalive: true }).catch(() => {});
                return clearSession();
            }
            try {
                // 관리자가 전체 세션을 초기화한 이후의 로그인 기록이 아니면 다시 로그인
                const { sessionInvalidateAt = 0 } = await fetch('/api/session-state').then(r => r.json());
                if (sessionInvalidateAt > LS.getFloat('breakChatLoginAt', 0)) return clearSession();
                const res = await apiFetch('/api/auth/me');
                if (!res.ok) return clearSession();
                const data = await res.json();
                setAdminId(data.adminId);
                await ensureFileToken();
                setUser(toUser(data));
                setStatus('authed');
            } catch {
                // 서버 연결 실패: 토큰은 유지하고 로그인 화면만 보여준다
                setStatus('anon');
            }
        })();
    }, [clearSession]);

    const value = useMemo(
        () => ({ status, user, signIn, signOut, replaceToken, updateUser }),
        [status, user, signIn, signOut, replaceToken, updateUser]
    );
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
